import {
  AfterViewInit,
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ResetPasswordEmailDto } from '@core/interfaces/email.interface';
import { EmailService } from '@core/services/email.service';
import { FormUtils } from '@core/utils/form-utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageService } from '@shared/services/language.service';
import { PlatformService } from '@shared/services/platform-service.service';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-request-reset-password',
  templateUrl: './request-reset-password.component.html',
  styleUrls: ['./request-reset-password.component.scss'],
  standalone: true,
  imports: [TranslateModule, FormsModule],
  host: { ngSkipHydration: 'true' },
})
@UntilDestroy()
export class RequestResetPasswordComponent
  implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
  @Input() prefillEmail: string = '';
  // New: allow parent to pass redirect path that will be appended to the reset URL
  @Input() redirectPath: string = '';

  resetPassword: ResetPasswordEmailDto = {
    email: '',
    languageCode: 'it',
    redirectPath: undefined,
  };
  private subscription: Subscription;
  isEmailInvalid = false;
  emailSent = false;
  platformService = inject(PlatformService);
  private languageService = inject(LanguageService);
  private emailService = inject(EmailService);
  private recaptchaV3Service = inject(ReCaptchaV3Service);

  ngOnChanges(changes: SimpleChanges): void {
    const c = changes['prefillEmail'];
    if (c) {
      const next = (c.currentValue ?? '').trim();
      const prev = (c.previousValue ?? '').trim();
      // Only set from prefill when it's provided and when the user hasn't typed a different value yet
      if (
        next &&
        (!this.resetPassword.email || this.resetPassword.email === prev)
      ) {
        this.resetPassword.email = next;
      }
    }
    const rp = changes['redirectPath'];
    if (rp) {
      this.resetPassword.redirectPath = (rp.currentValue ?? '').toString();
    }
  }

  ngOnInit(): void {
    if (this.prefillEmail) {
      this.resetPassword.email = this.prefillEmail;
    }
    if (this.redirectPath) {
      this.resetPassword.redirectPath = this.redirectPath;
    }
  }

  ngAfterViewInit() {
    this.platformService.runOnBrowser(() => {
      this.emailService.showReCaptchaBadge();
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.platformService.runOnBrowser(() => {
      this.emailService.hideReCaptchaBadge();
    });
  }

  onSubmit() {
    if (FormUtils.emailRegex.test(this.resetPassword.email)) {
      this.isEmailInvalid = false;
      this.resetPassword.languageCode =
        this.languageService.userSelectedLanguage.code;
      // Keep redirectPath as provided; backend will encode and include as query param
      this.subscription = this.recaptchaV3Service
        .execute('resetPassword')
        .subscribe((token: string) => {
          this.emailService
            .verifyReCaptcha({ responseToken: token })
            .pipe(untilDestroyed(this))
            .subscribe(() => {
              this.emailService
                .sendResetPasswordEmail(this.resetPassword)
                .pipe(untilDestroyed(this))
                .subscribe(() => {
                  this.emailSent = true;
                });
            });
        });
    } else {
      this.isEmailInvalid = true;
    }
  }
}
